import _ from 'lodash'
import httpClient from './services/httpClient'
import constants from './constants'
import request, { requests, Entity } from './utils/jsonapi'
import sendmail from './utils/sendmail'

import signupTpl from '../assets/templates/signupRequest.html'
function buildUrl(url, params) {
  const qs = _.map(
    params,
    (v, k) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`
  ).join('&')
  if (qs) {
    return `${url}?${qs}`
  }
  return url
}

const loginUrl = `${constants.API_ROOT}/${constants.API_LOGIN_PATH}`
const retailerUrl = `${constants.API_ROOT}/${constants.API_RETAILER_PATH}`
const inventoryUrl = `${constants.API_ROOT}/${constants.API_INVENTORY_PATH}`
const forgotPasswordUrl = `${constants.API_ROOT}/${constants.API_FORGOT_PASSWORD_PATH}`
const resetPasswordUrl = `${constants.API_ROOT}/${constants.API_RESET_PASSWORD_PATH}`


const uploadImageUrl = (entityType, imageType) => {
  if (imageType) {
    return `${constants.API_ROOT}/${entityType}/upload_image_${imageType}`
  }
  return `${constants.API_ROOT}/${entityType}/upload_image`
}

const uploadCSVUrl = `${retailerUrl}/upload_csv`
const retailerWithInventoryUrl = `${retailerUrl}/fetch_retailer`
const userUrl = `${retailerUrl}/fetch_user`

export default {
  setErrorHandler(wrap) {
    ;['get', 'post', 'put', 'patch', 'delete'].forEach(method => {
      const fn = request[method].bind(request)
      request[method] = wrap(fn)
    })
  },
  login(email, password) {
    return request.post(loginUrl, { username: email, password }, true)
  },
  forgotPassword(email, role) {
    return request.post(forgotPasswordUrl, { username: email, role }, true)
  },
  resetPassword(token, password) {
    return request.post(resetPasswordUrl, { token, password }, true)
  },
  register(data) {
    return sendmail(
      'Retailer Sign-up Request',
      constants.SUPPORT_EMAIL,
      _.template(signupTpl)(data)
    )
  },
  /**
   * The caller's own identity, plus a rotated session cookie.
   *
   * Replaces GET /refresh_token/{username}, which took the account to re-mint
   * from the URL and handed the JWT back in the body for the page to store.
   * Here the server reads the identity off the presented cookie and puts the
   * refreshed token straight back into it, so nothing passes through script.
   *
   * `options` is passed through to axios (auth.init uses it for a timeout).
   */
  session(options) {
    return httpClient.get(`${constants.API_ROOT}/session`, options).then(res => {
      const data = res.data && res.data.data
      return (data && data.attributes) || null
    })
  },
  /**
   * Ends the session server-side: blacklists the token and clears the cookies.
   * A cookie can only be cleared by the server that set it, so unlike the old
   * localStorage logout this cannot be done locally.
   */
  logout() {
    return httpClient.post(`${constants.API_ROOT}/logout`, {})
  },
  getRetailers(pageNumber, retailerName = '', stripeOnboarded = false) {
    const params = {
      'page[number]': pageNumber,
      'retailer_name': retailerName
    }
    // Only the Featured sellers list asks for this; the flag is left off
    // everywhere else so the Retailers list keeps returning every seller.
    if (stripeOnboarded) {
      params['stripe_onboarded'] = 1
    }
    return request.get(buildUrl(retailerWithInventoryUrl, params))
  },
  getUsers(pageNumber, retailerName = '') {
    return request.get(
      buildUrl(userUrl, {
        'page[number]': pageNumber,
        'retailer_name': retailerName
      })
    )
  },
  // The retailer picker. Goes through the admin-only user search rather than
  // the public Algolia index, which no longer carries account emails.
  searchRetailers(query) {
    return request.get(
      buildUrl(userUrl, { 'page[number]': 1, per_page: 8, retailer_name: query })
    )
  },
  getRetailer(retailerId) {
    return request.get(`${retailerUrl}/${retailerId}`)
  },
  getRetailerByCustomUrl(customUrl) {
    return request.get(`${retailerUrl}/custom_url/${customUrl}`)
  },
  addRetailer(data) {
    return request.post(retailerUrl, data)
  },
  updateRetailer(retailerId, data) {
    return request.patch(`${retailerUrl}/${retailerId}`, data)
  },
  deleteRetailer(retailerId) {
    return request.delete(`${retailerUrl}/${retailerId}`)
  },
  // `params` carries the optional server-side filters: live_seller_only,
  // indexed_only, search, setting, jewelry_type. Blank values are dropped so an
  // unset filter never reaches the API as "setting=undefined".
  getDiamonds(pageNumber, params) {
    const extra = _.omitBy(
      params || {},
      v => v === undefined || v === null || v === ''
    )
    return request.get(
      buildUrl(
        inventoryUrl,
        Object.assign({ 'page[number]': pageNumber, all_statuses: 1 }, extra)
      )
    )
  },
  getDiamond(diamondId) {
    return request.get(`${inventoryUrl}/${diamondId}`)
  },
  addDiamond(data) {
    return request.post(inventoryUrl, data)
  },
  updateDiamond(diamondId, data) {
    return request.patch(`${inventoryUrl}/${diamondId}`, data)
  },
  deleteDiamond(diamondId) {
    return request.delete(`${inventoryUrl}/${diamondId}`)
  },
  uploadImage(entityType, entityId, file) {
    const formData = new FormData()
    formData.append('images[]', file)
    return httpClient
      .post(`${uploadImageUrl(entityType)}/${entityId}`, formData, {
        headers: { 'content-type': 'multipart/form-data' }
      })
      .then(res => new Entity(res.data))
      .catch(err => {
        const out = new Error(err.message)
        out.status = err.response ? err.response.status : null
        out.response = err.response
        throw out
      })
  },
  deleteImage(typeUrl, entityId, images) {
    return request.delete(
      `${typeUrl}/${entityId}/relationships/images`,
      [].concat(images).map(image => _.pick(image, ['type', 'id']))
    )
  },
  deleteRetailerImage(retailerId, images) {
    const imageIds = [].concat(images).map(image => image.id)
    return request.delete(
      `${retailerUrl}/${retailerId}/relationships/images`,
      { image_ids: imageIds }
    )
  },
  deleteRetailerRepImage(retailerId) {
    return request.delete(`${retailerUrl}/${retailerId}/relationships/image_rep`)
  },
  deleteRetailerMastheadImage(retailerId) {
    return request.delete(`${retailerUrl}/${retailerId}/relationships/image_mast_head`)
  },
  deleteDiamondImage(diamondId, images) {
    return this.deleteImage(inventoryUrl, diamondId, images)
  },
  uploadRetailerRepImage(retailerId, file) {
    const formData = new FormData()
    formData.append('image', file)
    return httpClient
      .post(`${uploadImageUrl('retailer', 'rep')}/${retailerId}`, formData, {
        headers: { 'content-type': 'multipart/form-data' }
      })
      .then(res => new Entity(res.data))
      .catch(err => {
        const out = new Error(err.message)
        out.status = err.response ? err.response.status : null
        out.response = err.response
        throw out
      })
  },
  uploadRetailerLogoImage(retailerId, file) {
    const formData = new FormData()
    formData.append('image', file)
    return httpClient
      .post(`${uploadImageUrl('retailer', 'logo')}/${retailerId}`, formData, {
        headers: { 'content-type': 'multipart/form-data' }
      })
      .then(res => new Entity(res.data))
      .catch(err => {
        const out = new Error(err.message)
        out.status = err.response ? err.response.status : null
        out.response = err.response
        throw out
      })
  },
  uploadRetailerMastheadImage(retailerId, file) {
    const formData = new FormData()
    formData.append('image', file)
    return httpClient
      .post(`${uploadImageUrl('retailer', 'mast_head')}/${retailerId}`, formData, {
        headers: { 'content-type': 'multipart/form-data' }
      })
      .then(res => new Entity(res.data))
      .catch(err => {
        const out = new Error(err.message)
        out.status = err.response ? err.response.status : null
        out.response = err.response
        throw out
      })
  },
  csvUpload(retailerId, file) {
    const formData = new FormData()
    formData.append('csv', file)
    return httpClient
      .post(`${uploadCSVUrl}/${retailerId}`, formData, {
        headers: { 'content-type': 'multipart/form-data' }
      })
      .then(res => new Entity(res.data))
      .catch(err => {
        const out = new Error(err.message)
        out.status = err.response ? err.response.status : null
        out.response = err.response
        throw out
      })
  },
  addFeature(data) {
    return requests.post(`${constants.API_ROOT}/featured/add_sellers`, data)
  },
  deleteComment(data) {
    return requests.post(`${constants.API_ROOT}/admin/delete_comment`, data)
  },
  deleteReview(data) {
    return requests.post(`${constants.API_ROOT}/admin/delete_review`, data)
  },
  deleteProduct(data) {
    return requests.post(`${constants.API_ROOT}/admin/delete_product`, data)
  },

  viewReviews(productId) {
    return requests.get(`${constants.API_ROOT}/admin/get_reviews/${productId}`)
  },
  viewComments(productId) {
    return requests.get(`${constants.API_ROOT}/admin/view_comments/${productId}`)
  },
  removeFeature(data) {
    return requests.post(`${constants.API_ROOT}/featured/remove_seller`, data)
  },
  allRetailers(page) {
    return requests.get(`${constants.API_ROOT}/retailer/fetch_retailer?page=${page}`)
  },
  removeBestCollection(data) {
    return requests.post(`${constants.API_ROOT}/admin/best_col/remove_product`, data)
  },
  addBestCollection(data) {
    return requests.post(`${constants.API_ROOT}/admin/best_col/add_col`, data)
  },
  getBestCollection() {
    return requests.get(`${constants.API_ROOT}/admin/get_best_design`)
  },
  getCommentedItems() {
    return httpClient
      .get(`${constants.API_ROOT}/admin/get_comment_ids/1`)
      .then(res => res.data)
  },

  // get commented items with filter and retailer name
  getCommentedItemsWithFilter(page, filter, retailerName) {
    return httpClient
      .get(`${constants.API_ROOT}/admin/filter_get_comment_ids/${page}/${filter}/${retailerName}`)
      .then(res => res.data)
  },

  getReviewedItems(page) {
    return httpClient
      .get(`${constants.API_ROOT}/admin/get_review_ids/${page}`)
      .then(res => res.data)
  },

  // get commented items with filter and retailer name
  getReviewedItemsWithFilter(page, filter, retailerName, sortBy, sortDir) {
    const params = {}
    if (sortBy) params.sort_by = sortBy
    if (sortDir) params.sort_dir = sortDir
    const query = Object.keys(params).length
      ? `?${new URLSearchParams(params).toString()}`
      : ''
    return httpClient
      .get(`${constants.API_ROOT}/admin/filter_get_review_ids/${page}/${filter}/${retailerName}${query}`)
      .then(res => res.data)
  },

  getPromotionSettings() {
    return httpClient
      .get(`${constants.API_ROOT}/admin/promotion_settings`)
      .then(res => res.data)
  },

  updatePromotionSetting(id, data) {
    return httpClient
      .patch(`${constants.API_ROOT}/admin/promotion_settings/${id}`, data)
      .then(res => res.data)
  },

  updatePromotionSettingStatus(id, data) {
    return httpClient
      .patch(`${constants.API_ROOT}/admin/promotion_settings/${id}/status`, data)
      .then(res => res.data)
  },


}



